import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { canAccessStaffComplianceAdmin } from '../../utils/staffComplianceAccess';
import StructuredAppraisalForm from './StructuredAppraisalForm';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, CalendarClock, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatBackendDate } from '../../lib/dateUtils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  self_assessment_pending: 'Awaiting self-assessment',
  self_assessment_submitted: 'Self-assessment received',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export default function AppraisalsPanel({ employeeId, employeeName = '' }) {
  const { token, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [structuredOpen, setStructuredOpen] = useState(false);
  const [activeCycleId, setActiveCycleId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ scheduled_at: '', reviewer: '', notes: '' });

  const isAdminUser = canAccessStaffComplianceAdmin(user);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchItems = async () => {
    if (!employeeId || !token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/appraisals`, { headers });
      setItems(response.data?.items || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load appraisals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [employeeId, token]);

  const scheduleAppraisal = async () => {
    if (!scheduleForm.scheduled_at || !scheduleForm.reviewer.trim()) {
      toast.error('Meeting date and reviewer are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/appraisals/schedule`,
        {
          scheduled_at: scheduleForm.scheduled_at,
          reviewer: scheduleForm.reviewer.trim(),
          notes: scheduleForm.notes.trim() || null,
        },
        { headers },
      );
      toast.success('Appraisal booked — worker will see it on their dashboard');
      setScheduleOpen(false);
      setScheduleForm({ scheduled_at: '', reviewer: '', notes: '' });
      fetchItems();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to schedule appraisal');
    } finally {
      setSaving(false);
    }
  };

  const openReviewForm = (cycleId) => {
    setActiveCycleId(cycleId);
    setStructuredOpen(true);
  };

  const openAppraisalPdf = async (recordId) => {
    setPdfLoading(recordId);
    try {
      const response = await axios.get(
        `${API}/employees/${employeeId}/appraisals/${recordId}/download-pdf`,
        { headers, responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error('Failed to open appraisal PDF');
    } finally {
      setPdfLoading(null);
    }
  };

  const handleStructuredSaved = async (result) => {
    await fetchItems();
    if (result?.id) await openAppraisalPdf(result.id);
  };

  return (
    <div className="space-y-6" data-testid="appraisals-panel">
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-lg">Appraisals</CardTitle>
            <CardDescription>
              Annual performance cycle for {employeeName || 'employee'} — self-assessment, review, and SDP.
              Same structured appraisal forms for all staff.
            </CardDescription>
          </div>
          {isAdminUser && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setScheduleOpen(true)}>
                <CalendarClock className="h-4 w-4 mr-2" /> Book appraisal
              </Button>
              <Button onClick={() => { setActiveCycleId(null); setStructuredOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Start appraisal form
              </Button>
            </div>
          )}
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="text-sm text-gray-500">Loading appraisals...</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-gray-500">
              No appraisal cycles yet. Book a meeting so the worker can complete their self-assessment on the dashboard.
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.cycle_id} className="rounded-lg border border-gray-200 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.scheduled_at
                          ? `Meeting ${formatBackendDate(item.scheduled_at)}`
                          : item.appraisal_date
                            ? `Completed ${formatBackendDate(item.appraisal_date)}`
                            : 'Annual appraisal'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Reviewer: {item.reviewer || '—'}
                        {item.next_due_at ? ` · Next due ${formatBackendDate(item.next_due_at)}` : ''}
                      </p>
                    </div>
                    <Badge variant="outline">{STATUS_LABELS[item.status] || item.status}</Badge>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant={item.self_assessment ? 'default' : 'secondary'}>
                      Self-assessment {item.self_assessment ? 'submitted' : 'pending'}
                    </Badge>
                    <Badge variant={item.annual_review ? 'default' : 'secondary'}>
                      Annual review {item.annual_review ? 'complete' : 'pending'}
                    </Badge>
                    {item.development_plan && (
                      <Badge variant="outline">SDP on file</Badge>
                    )}
                  </div>

                  {isAdminUser && (
                    <div className="flex flex-wrap gap-2">
                      {item.status === 'self_assessment_submitted' && (
                        <Button size="sm" onClick={() => openReviewForm(item.cycle_id)}>
                          Complete annual review
                        </Button>
                      )}
                      {item.self_assessment?.id && (
                        <Button size="sm" variant="outline"
                          onClick={() => openAppraisalPdf(item.self_assessment.id)}
                          disabled={pdfLoading === item.self_assessment.id}>
                          {pdfLoading === item.self_assessment.id
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <FileText className="h-4 w-4 mr-1" />}
                          Self-assessment PDF
                        </Button>
                      )}
                      {item.annual_review?.id && (
                        <Button size="sm" variant="outline"
                          onClick={() => openAppraisalPdf(item.annual_review.id)}
                          disabled={pdfLoading === item.annual_review.id}>
                          Review PDF
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book annual appraisal</DialogTitle>
            <DialogDescription>
              The worker will see the meeting date on their dashboard and can complete the self-assessment form beforehand.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Appraisal meeting date *</Label>
              <Input type="date" value={scheduleForm.scheduled_at}
                onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_at: e.target.value })} />
            </div>
            <div>
              <Label>Reviewer / manager *</Label>
              <Input value={scheduleForm.reviewer}
                onChange={(e) => setScheduleForm({ ...scheduleForm, reviewer: e.target.value })} />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={scheduleForm.notes}
                onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={scheduleAppraisal} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Book appraisal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdminUser && (
        <StructuredAppraisalForm
          open={structuredOpen}
          onOpenChange={setStructuredOpen}
          employeeId={employeeId}
          token={token}
          mode="admin"
          cycleId={activeCycleId}
          initialTemplateId={activeCycleId ? 'annual_performance_review' : null}
          onSuccess={handleStructuredSaved}
        />
      )}
    </div>
  );
}
